import { Link } from "react-router-dom";
import { FlagDivider, Text, Section } from "@/components";
import { useApp } from "@/hooks";
import { DASHBOARD_SECTIONS } from "@/utils";
import type { LinkItem } from "@/utils";


export const GetStartedSection = () => {
  const { setShowContactSalesModal, goToSection, doSignIn } = useApp();

  const MOBILE_DASHBOARD_SECTIONS: LinkItem[] = [
    {
      id: "features-section",
      title: "Features",
    },
    {
      id: "our-vision-section",
      title: "About Us",
    },
    {
      id: "how-it-works-section",
      title: "How it Works",
      icon: "rooms",
    },
    {
      id: "faq-section",
      title: "FAQ",
    },
    {
      id: "key-benefits-section",
      title: "Solutions",
    },
    {
      id: "contact-sales-section",
      title: "Contact Sales",
      onClick: () => {
        setShowContactSalesModal(true);
      }
    },
    {
      id: "pricing-section",
      title: "Pricing",
    },
    {
      id: "login-section",
      title: "Login",
      onClick: () => {
        doSignIn();
      }
    },
  ];

  const onClickLink = (item: LinkItem) => {
    if (item.onClick) {
      item.onClick();
    } else {
      goToSection(item.id);
    }
  }


  return (
    <Section
      id="get-started-section"
      className="flex flex-col items-center gap-2.5 overflow-hidden w-full px-[36px] md:px-0 bg-black"
    >
      <div className="w-full max-w-[1231px]">
        <FlagDivider />
      </div>
      
      <div className="mt-[55px] md:mt-[37px]">
        <img
          src="/images/home/intelliroom-logo.svg"
          alt="Get Started"
          loading="lazy" // Added lazy loading
        />
      </div>

      <div className="font-dmSans font-normal text-center w-full max-w-[306px] md:max-w-[684px] my-0 md:my-2.5">
        {/* desktop version */}
        <Text.Description size="md" className="hidden sm:block text-[#D7D7D7]">
          The IntelliRoom™ surfaces factors most likely to <strong className="text-white">enhance, or erode, investment value</strong>, so you can make faster & more informed decisions.
        </Text.Description>

        {/* Mobile version */}
        <Text.Description size="md" className="block sm:hidden text-[#D7D7D7]">
          The IntelliRoom™ surfaces factors most likely to <strong className="text-white">enhance, or erode, investment value</strong>, so you can make faster & more informed decisions.
        </Text.Description>
      </div>
      

      {/* desktop version */}
      <div className="w-full md:max-w-[757px] hidden md:flex justify-between items-center px-0 md:px-11 my-5 md:my-2.5">
        <div className="flex gap-4 mt-0.5">
          {DASHBOARD_SECTIONS.map((section, index) => (
            <Link
              key={`footer-section-${index}`}
              to="#"
              className="!text-[#D9D9D9] text-[14px] leading-[100%] font-400 get-started-link"
              onClick={() => onClickLink(section)}
            >{section.title}</Link>
          ))}
          
          <Link to="https://www.t12.ai/cap-table" className="text-[14px] leading-[100%] get-started-link">Cap Table</Link>
        </div>

        <div className="flex">
          <Link to="https://x.com/t12_ai" className="mr-4">
            <img
              src="/images/home/x.svg"
              alt="X"
              loading="lazy" // Added lazy loading
              onMouseOver={e => e.currentTarget.src = "/images/home/x-hover.svg"}
              onMouseOut={e => e.currentTarget.src = "/images/home/x.svg"}
            />
          </Link>
          <Link to="https://www.linkedin.com/company/t12ai/">
            <img
              src="/images/home/linkedin.svg"
              alt="LinkedIn"
              loading="lazy" // Added lazy loading
              onMouseOver={e => e.currentTarget.src = "/images/home/linkedin-hover.svg"}
              onMouseOut={e => e.currentTarget.src = "/images/home/linkedin.svg"}
            />
          </Link>
        </div>
      </div>

      {/* Mobile version */}
      <div className="block md:hidden mt-6">
        <div className="flex justify-center mb-[30px]">
          <Link to="https://x.com/t12_ai" className="mr-4 pr-4 border-r border-[#454545]">
            <img
              src="/images/home/x.svg"
              alt="X"
              loading="lazy" // Added lazy loading
              onMouseOver={e => e.currentTarget.src = "/images/home/x-hover.svg"}
              onMouseOut={e => e.currentTarget.src = "/images/home/x.svg"}
            />
          </Link>

          <Link to="https://www.linkedin.com/company/t12ai/">
            <img
              src="/images/home/linkedin.svg"
              alt="LinkedIn"
              loading="lazy" // Added lazy loading
              onMouseOver={e => e.currentTarget.src = "/images/home/linkedin-hover.svg"}
              onMouseOut={e => e.currentTarget.src = "/images/home/linkedin.svg"}
            />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-4 md:hidden">
          {MOBILE_DASHBOARD_SECTIONS.map((section, index) => (
            <Link
              key={`footer-section-${index}`}
              to="#"
              className="text-[18px] font-inter font-400"
              onClick={() => onClickLink(section)}
            >{section.title}</Link>
          ))}
        </div>
      </div>

      <div className="get-started-link-item block w-full md:max-w-[757px] mt-8 md:mt-7 px-0 md:px-12 border-b border-solid border-[#727070] pt-0 md:pt-10">
        <div className="flex justify-between items-end pb-4 md:pb-7">
          <Text.Heading
            size="sm"
            className="text-center text-default !text-[24px] md:!text-[42px] hover:text-[#33DAE1] font-calSans font-400 md:font-semibold cursor-pointer footer-get-started-text"
            onClick={() => {
              window.open("https://app.intelliroom.ai/auth/sign-up", "_blank");
            }}
          >
            Get Start - 14 Day Free Trial
          </Text.Heading>
          <Link className="link-img-wrapper" to="https://app.intelliroom.ai/auth/sign-up">
            <img 
              src="/images/home/vector-up-default.svg" 
              alt="Get Started"
              loading="lazy" // Added lazy loading
              className="w-7 h-7 md:w-12 md:h-12 default" 
            />

            <img 
              src="/images/home/vector-up-hover.svg" 
              alt="Get Started"
              loading="lazy" // Added lazy loading
              className="w-7 h-7 md:w-12 md:h-12 hover" 
            />
          </Link>
        </div>
      </div>
    </Section>
  );
};
